import DeliveryFeeComparison from 'src/components/invoices/DeliveryFeeComparison'
import InvoiceDiscrepancyAction from './InvoiceDiscrepancyAction'
import React, { useEffect, useMemo, useState } from 'react'
import { Button, Modal, Popconfirm, Tag } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import styled from 'styled-components/macro'
import { stripTransientProps } from 'src/utils/stripTransientProps'
import { useSelector, useDispatch } from 'react-redux'
import PurchaseOrderInput from 'src/components/invoices/PurchaseOrderInput'
import {
  formatInvoiceTotalDisplay,
  formatSupplierTitleDisplay,
  getEngineRecommendedPurchaseOrderNumber,
  getInvoiceDocumentPurchaseOrderNumber,
  getSuggestedLinkedOrderId,
} from 'src/utils/invoiceUploadHelpers'
import {
  getInvoiceMatchActionMeta,
  getMatchModalActionPresentation,
  getFixDetailsFromMatchMeta,
  canOfferMatchAndReconcileForSelection,
  hasIdentifiedOrderForInvoiceLink,
  MATCH_ACTION_PRIMARY,
  MATCH_ACTION_TONE,
} from 'src/utils/invoiceMatchActionHelpers'
import {
  linkInvoiceToPurchaseOrder,
  reconcileInvoiceOneClick,
  removeInvoiceFromPurchaseOrder,
  searchPurchaseOrders,
} from 'src/features/NewInvoicesSlice'
import { savedAllocationsMatchInvoiceTotal } from 'src/utils/invoicePoAllocationHelpers'
import { autoSendReconciledInvoiceToXero } from 'src/features/ReceiptingSlice'
import {
  isAlreadyReconciledError,
  isForceReconcileRequiredError,
  isInvoiceLinkedToMultipleOrdersError,
  getInvoiceLinkedToMultipleOrdersMessage,
  getMatchAndReconcileFailureTitle,
  getReconcileFailureDescription,
} from 'src/utils/whyThisMatchHelpers'
import { getInvoicePoSearchSupplierId } from 'src/utils/invoicePoSearchHelpers'
import {
  collectMatchSelectedOrders,
  buildMatchOrdersFromInvoice,
  createMatchExtraOrderRow,
  getDuplicateMatchPoMessage,
  getSentToXeroMatchWarning,
  isMatchMultiOrderDraft,
  shouldShowAddAnotherOrderInMatchModal,
} from 'src/utils/invoiceMatchExtraOrdersHelpers'
import {
  applyMatchPoSuggestionsToForm,
  getMatchPoSuggestionsPresentation,
} from 'src/utils/invoiceMatchPoSuggestionsHelpers'
import { getInvoiceMatchedOrdersTableDisplay } from 'src/utils/invoiceMatchedOrdersDisplayHelpers'
import { findPoReconciliationConflictInLibrary } from 'src/utils/invoicePoReconciliationConflictHelpers'
import AllocateInvoiceToPosModal from './AllocateInvoiceToPosModal'
import {
  canShowNotThisOrderInMatchModal,
  getRemoveFromPoActionMeta,
} from 'src/utils/invoiceRemoveFromPoHelpers'
import {
  RejectedNotification,
  SuccessNotification,
} from 'src/notifications/SuccessNotification'
import {
  getNoOrderGuidanceLinks,
  getNoOrderMatchGuidance,
  shouldShowNoOrderMatchGuidance,
} from 'src/utils/invoiceNoOrderGuidance'
import {
  getCreditNoteApplySuccessCopy,
  isCreditNoteDocument,
  buildPoNetAmbientSummary,
  getInvoiceCreditNoteCoachSuggestion,
} from 'src/utils/creditNoteDocumentHelpers'
import { getLinkedInvoicesForReceiptingOrder } from 'src/utils/reconciliationHealth'
import { buildInvoicesPath } from 'src/utils/reconciliationHealthApi'
import { Link } from 'react-router-dom'
import {
  toastPostReconcileXeroAutoSend,
  XERO_AUTO_SEND_PARTIAL_WAIT_COPY,
} from 'src/utils/xeroAutoSendCopy'
import {
  shouldAutoSendAfterReconcile,
  XERO_AUTO_SEND_SKIP_REASON,
} from 'src/utils/xeroAutoSendBill'

function resolveRecommendedPo(invoice = {}) {
  return getEngineRecommendedPurchaseOrderNumber(invoice)
}

function resolveInvoiceDocumentPo(invoice = {}) {
  return getInvoiceDocumentPurchaseOrderNumber(invoice)
}

function buildLinkBody({
  purchaseOrderNumber,
  linkedOrderId,
  selectedIsRecommended,
  recommendedPo,
  recommendedOrderId,
  supplierId,
  // When the engine suggested multiple POs, never soft-link via useRecommended
  // alone — the user may have dropped the incorrect PO in the modal.
  engineMatchedMultiplePos = false,
}) {
  const po = String(purchaseOrderNumber || '').trim()
  const supplier =
    supplierId != null && String(supplierId).trim()
      ? String(supplierId).trim()
      : undefined
  const orderId = linkedOrderId || recommendedOrderId || undefined
  if (
    selectedIsRecommended &&
    (recommendedPo || recommendedOrderId) &&
    !engineMatchedMultiplePos
  ) {
    return {
      useRecommended: true,
      purchaseOrderNumber: po || recommendedPo,
      linkedOrderId: orderId,
      ...(supplier ? { supplierId: supplier } : {}),
    }
  }
  return {
    purchaseOrderNumber: po,
    linkedOrderId: orderId,
    ...(supplier ? { supplierId: supplier } : {}),
  }
}

/**
 * Match modal: recommended PO + alternatives.
 * Green (recommended PO selected) → Match & reconcile
 * Amber / red / alternate PO → Link this invoice only
 */
function MatchInvoiceModal({
  visible,
  venueId,
  invoice,
  username,
  uploadedInvoices = [],
  onCancel,
  onLinked,
  onReconciled,
  onRemoved,
  onFixDetails,
}) {
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('')
  const [linkedOrderId, setLinkedOrderId] = useState(null)
  const [extraOrders, setExtraOrders] = useState([])
  const [allocateSeedSelections, setAllocateSeedSelections] = useState(null)
  const [showAllocateStep, setShowAllocateStep] = useState(false)
  const [saving, setSaving] = useState(false)
  const dispatch = useDispatch()
  const receiptingOrders = useSelector(
    state => state.receipting?.receiptingOrders || [],
  )
  const venue = useSelector(state => state.venue?.venue || {})

  const matchAction = getInvoiceMatchActionMeta(invoice || {})
  // Goods receipting has no Edit invoice modal, so it passes no handler.
  const canFixDetailsHere = typeof onFixDetails === 'function'
  const fixDetails = getFixDetailsFromMatchMeta(invoice || {}, {
    canFixDetailsHere,
  })
  const removeFromPo = getRemoveFromPoActionMeta(invoice || {})
  const recommendedPo = resolveRecommendedPo(invoice || {})
  const invoiceDocumentPo = resolveInvoiceDocumentPo(invoice || {})
  const recommendedOrderId = recommendedPo
    ? getSuggestedLinkedOrderId(invoice || {})
    : null
  const selectedIsRecommended =
    Boolean(recommendedPo) &&
    String(purchaseOrderNumber || '').trim() === recommendedPo
  const selectedIsInvoiceDocumentPo =
    !selectedIsRecommended &&
    Boolean(invoiceDocumentPo) &&
    String(purchaseOrderNumber || '').trim() === invoiceDocumentPo

  // Green recommended path, or deliberate order pick when PO collides.
  // Credit notes never offer Match & reconcile.
  const canMatchAndReconcile = canOfferMatchAndReconcileForSelection(
    invoice || {},
    {
      selectedIsRecommended,
      linkedOrderId,
    },
  )
  const deliberateOrderPick =
    canMatchAndReconcile &&
    matchAction.primaryAction !== MATCH_ACTION_PRIMARY.MATCH_AND_RECONCILE
  const presentation = getMatchModalActionPresentation(invoice || {}, {
    canMatchAndReconcile,
    selectedIsRecommended,
    deliberateOrderPick,
  })
  const isCreditNote = isCreditNoteDocument(invoice || {})
  const ambientPo =
    String(purchaseOrderNumber || '').trim() ||
    recommendedPo ||
    invoiceDocumentPo ||
    String(invoice?.purchaseOrderNumber || '').trim()
  const linkedOnAmbientPo = ambientPo
    ? getLinkedInvoicesForReceiptingOrder(
        { purchaseOrderNumber: ambientPo },
        uploadedInvoices?.length ? uploadedInvoices : invoice ? [invoice] : [],
      )
    : []
  const netAmbientLine = buildPoNetAmbientSummary({
    linkedDocuments: linkedOnAmbientPo,
    remainingPennies:
      invoice?.matchSummary?.remainingBalancePennies == null
        ? null
        : Number(invoice.matchSummary.remainingBalancePennies),
  })
  const matchingOrder = useMemo(() => {
    const po = String(ambientPo || '').trim()
    if (!po) return null
    return (
      (receiptingOrders || []).find(
        order => String(order?.purchaseOrderNumber || '').trim() === po,
      ) || null
    )
  }, [ambientPo, receiptingOrders])

  const maybeAutoSendToXero = async ({
    result,
    purchaseOrderNumber: reconciledPo,
  }) => {
    if (!shouldAutoSendAfterReconcile(result)) {
      return { showedSentSuccess: false }
    }

    const po = String(reconciledPo || '').trim()
    let order = matchingOrder
    if (
      !order ||
      String(order?.purchaseOrderNumber || '').trim() !== po ||
      !Array.isArray(order?.orderIds)
    ) {
      const candidates = await searchPurchaseOrders(
        venueId,
        po,
        invoice?.supplier,
      )
      order =
        candidates.find(
          candidate =>
            String(candidate?.purchaseOrderNumber || '').trim() === po,
        ) || null
    }

    if (!order) {
      return toastPostReconcileXeroAutoSend({
        autoSendResult: {
          sent: false,
          reason: XERO_AUTO_SEND_SKIP_REASON.ORDER_NOT_FOUND,
        },
        venue,
      })
    }

    const orderIds =
      result?.linked?.orderIds?.length > 0
        ? result.linked.orderIds
        : order.orderIds
    const venueSupplier = order?.venueSupplier
    try {
      const autoSendResult = await dispatch(
        autoSendReconciledInvoiceToXero({
          venue,
          order: { ...order, orderIds },
          venueSupplier,
          invoiceId: invoice.id,
          invoice,
        }),
      )
      return toastPostReconcileXeroAutoSend({
        autoSendResult,
        venue,
        venueSupplier,
        order,
      })
    } catch (xeroError) {
      // Reconciliation is authoritative; Xero remains a retryable side effect.
      console.error('Post-reconcile Xero auto-send failed', xeroError)
      return toastPostReconcileXeroAutoSend({
        error: xeroError,
        venue,
        venueSupplier,
        order,
      })
    }
  }
  const cnCoach = useMemo(
    () =>
      getInvoiceCreditNoteCoachSuggestion({
        invoice: invoice || {},
        order: matchingOrder,
        linkedDocuments: linkedOnAmbientPo,
      }),
    [invoice, matchingOrder, linkedOnAmbientPo],
  )
  const uploadCreditNotePath =
    venueId && ambientPo
      ? buildInvoicesPath(venueId, {
          upload: true,
          po: ambientPo,
          supplierId: invoice?.supplierId || invoice?.supplier || '',
          supplierTitle: invoice?.supplierTitle || '',
        })
      : venueId
      ? buildInvoicesPath(venueId, { upload: true })
      : null
  const browseCreditsPath = venueId
    ? buildInvoicesPath(venueId, {
        q: ambientPo || invoice?.invoiceNumber || '',
      })
    : null
  const fixDetailsInvoicesPath = venueId
    ? buildInvoicesPath(venueId, {
        q:
          invoice?.invoiceNumber ||
          invoice?.originalFilename ||
          ambientPo ||
          '',
      })
    : null
  const showNotThisOrder = canShowNotThisOrderInMatchModal(invoice || {}, {
    selectedPurchaseOrderNumber: purchaseOrderNumber,
    recommendedPurchaseOrderNumber: recommendedPo || invoiceDocumentPo,
  })
  const noOrderLinks = getNoOrderGuidanceLinks(venueId, {
    invoiceId: invoice?.id,
    purchaseOrderNumber: invoice?.purchaseOrderNumber,
  })
  const showNoOrderGuidance = shouldShowNoOrderMatchGuidance(invoice || {})
  const noOrderGuidance = showNoOrderGuidance
    ? getNoOrderMatchGuidance(invoice || {})
    : null
  useEffect(() => {
    if (!visible || !invoice) return
    const seeded = buildMatchOrdersFromInvoice(invoice)
    const initialPo =
      seeded?.purchaseOrderNumber || recommendedPo || invoiceDocumentPo || ''
    setPurchaseOrderNumber(initialPo)
    setLinkedOrderId(
      seeded?.linkedOrderId ||
        invoice.linkedOrderId ||
        (initialPo === recommendedPo ? recommendedOrderId : null) ||
        null,
    )
    setExtraOrders(seeded?.extraOrders || [])
    setAllocateSeedSelections(null)
    setShowAllocateStep(false)
  }, [visible, invoice, recommendedPo, invoiceDocumentPo, recommendedOrderId])

  const handlePoChange = update => {
    const nextPo = update.purchaseOrderNumber || ''
    const duplicate = getDuplicateMatchPoMessage(
      nextPo,
      extraOrders.map(row => row.purchaseOrderNumber),
    )
    if (duplicate) {
      RejectedNotification({
        message: 'Purchase order already added',
        description: duplicate,
      })
      return
    }
    setPurchaseOrderNumber(nextPo)
    setLinkedOrderId(update.linkedOrderId || null)
  }

  const handleUseRecommended = () => {
    if (!recommendedPo && !recommendedOrderId) return
    setPurchaseOrderNumber(recommendedPo)
    setLinkedOrderId(recommendedOrderId || null)
  }

  const handleUseInvoiceDocumentPo = () => {
    if (!invoiceDocumentPo) return
    // Prefill the search box only — linking still requires selecting an
    // identified OMS order (PO text alone is not unique across suppliers).
    setPurchaseOrderNumber(invoiceDocumentPo)
    setLinkedOrderId(null)
    RejectedNotification({
      message: 'Select the matching OMS order',
      description:
        'The document PO was copied into search. Choose the order for this supplier from the results — the same PO number may exist for another supplier.',
    })
  }

  const handleUseSuggestedOrders = () => {
    const applied = applyMatchPoSuggestionsToForm(
      poSuggestions.suggestions,
      invoice || {},
    )
    if (!applied) return
    setPurchaseOrderNumber(applied.purchaseOrderNumber)
    setLinkedOrderId(applied.linkedOrderId)
    setExtraOrders(applied.extraOrders)
  }

  const requireSelectedPo = () => {
    const po = String(purchaseOrderNumber || '').trim()
    if (!po && !linkedOrderId) {
      RejectedNotification({
        message: 'Choose a purchase order',
        description: recommendedPo
          ? 'Use the recommended PO, or search and select another order.'
          : 'Search and select a purchase order to continue.',
      })
      return null
    }
    if (
      !hasIdentifiedOrderForInvoiceLink({
        linkedOrderId,
        selectedIsRecommended,
        recommendedOrderId,
      })
    ) {
      RejectedNotification({
        message: 'Select an identified OMS order',
        description:
          'Purchase order number alone is not unique across suppliers. Search and pick the order that belongs to this invoice’s supplier.',
      })
      return null
    }
    const engineMatchedMultiplePos =
      Array.isArray(invoice?.matchedPurchaseOrderNumbers) &&
      invoice.matchedPurchaseOrderNumbers
        .map(value => String(value || '').trim())
        .filter(Boolean).length >= 2

    return buildLinkBody({
      purchaseOrderNumber: po,
      linkedOrderId,
      selectedIsRecommended,
      recommendedPo,
      recommendedOrderId,
      supplierId: poSearchSupplierId,
      engineMatchedMultiplePos,
    })
  }

  const handleLinkOnly = async () => {
    const body = requireSelectedPo()
    if (!body || !invoice?.id) return

    const poLabel =
      String(body.purchaseOrderNumber || purchaseOrderNumber || '').trim() ||
      'the selected PO'

    setSaving(true)
    try {
      const response = await linkInvoiceToPurchaseOrder(invoice.id, body)
      const linked = response?.linked || response?.data?.linked || {}
      if (isCreditNote || linked.creditApplied) {
        SuccessNotification({
          message: 'Credit applied',
          description: getCreditNoteApplySuccessCopy(
            linked.creditPenniesAbs != null
              ? linked.creditPenniesAbs
              : invoice.invoiceTotal != null
              ? Math.abs(Number(invoice.invoiceTotal))
              : null,
            linked.remainingPennies ??
              invoice.matchSummary?.remainingBalancePennies ??
              null,
            { waitingForInvoice: Boolean(linked.waitingForInvoice) },
          ),
        })
        try {
          const { maybePromptAndCloseCreditRequestAfterCnApply } = await import(
            'src/utils/maybePromptAndCloseCreditRequestAfterCnApply'
          )
          const poForBridge =
            String(
              body.purchaseOrderNumber || purchaseOrderNumber || '',
            ).trim() || ambientPo
          await maybePromptAndCloseCreditRequestAfterCnApply({
            dispatch,
            venueId,
            purchaseOrderNumber: poForBridge,
            order: matchingOrder,
          })
        } catch (bridgeError) {
          console.error(bridgeError)
        }
      } else {
        SuccessNotification({
          message: 'Invoice attached',
          description: `Attached to ${poLabel}. Status is now Invoice attached — this is not Reconciled yet. Finish with Match & reconcile when totals align, or Full accept on Goods Receipting.`,
        })
      }
      onLinked?.({
        outcome:
          isCreditNote || linked.creditApplied ? 'credit_applied' : 'linked',
        purchaseOrderNumber: poLabel,
        data: response?.data || response,
      })
    } catch (error) {
      RejectedNotification({
        message: isCreditNote
          ? 'Could not apply credit note'
          : 'Could not link invoice',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Please try again.',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleMatchAndReconcile = async () => {
    const allocationsAlreadyBalance = savedAllocationsMatchInvoiceTotal(invoice)
    const savedPo = allocationsAlreadyBalance
      ? String(
          (invoice.poAllocations || []).find(row => !row?.reconciledAt)
            ?.purchaseOrderNumber ||
            invoice.poAllocations?.[0]?.purchaseOrderNumber ||
            '',
        ).trim()
      : ''
    const body = allocationsAlreadyBalance
      ? { purchaseOrderNumber: savedPo }
      : requireSelectedPo()
    if (!body || !invoice?.id) return

    const poLabel =
      String(body.purchaseOrderNumber || purchaseOrderNumber || '').trim() ||
      'the recommended PO'

    setSaving(true)
    let linkedOk = false
    try {
      // A saved split that already equals the invoice total is the plan.
      // Linking one PO again drops the other amounts and asks the chef to allocate.
      if (!allocationsAlreadyBalance) {
        await linkInvoiceToPurchaseOrder(invoice.id, body)
        linkedOk = true
      }

      const result = await reconcileInvoiceOneClick(invoice.id, {
        force: false,
        purchaseOrderNumber: body.purchaseOrderNumber || purchaseOrderNumber,
      })
      const fullyDone = result?.invoiceFullyReconciled !== false
      const openPos = result?.openPurchaseOrderNumbers || []
      const closedPos = result?.closedPurchaseOrderNumbers || []
      const closedLabel = closedPos.length ? closedPos.join(', ') : poLabel
      const { showedSentSuccess } = await maybeAutoSendToXero({
        result,
        purchaseOrderNumber:
          result?.linked?.purchaseOrderNumber ||
          body.purchaseOrderNumber ||
          purchaseOrderNumber,
      })
      if (result?.alreadyReconciled) {
        SuccessNotification({
          message: 'Already reconciled',
          description:
            'This invoice was already reconciled. Refreshing your list — Status should show Reconciled.',
        })
        onReconciled?.({ outcome: 'already_reconciled', data: result })
      } else if (result?.alreadyReconciledAllocation && !fullyDone) {
        SuccessNotification({
          message: 'Slice already reconciled',
          description: `${poLabel} was already closed. Still open on ${openPos.join(
            ', ',
          )}. ${XERO_AUTO_SEND_PARTIAL_WAIT_COPY}`,
        })
        onReconciled?.({
          outcome: 'allocation_already_reconciled',
          purchaseOrderNumber: poLabel,
          data: result?.data || result,
        })
      } else if (result?.recovered) {
        if (!(showedSentSuccess && fullyDone)) {
          SuccessNotification({
            message: fullyDone ? 'Matched & reconciled' : 'PO slice reconciled',
            description: fullyDone
              ? `Goods Receipting was already Full accept for ${poLabel}. Invoice Status is now Reconciled.`
              : `Goods Receipting was already Full accept for ${poLabel}. Invoice still open on ${openPos.join(
                  ', ',
                )}. ${XERO_AUTO_SEND_PARTIAL_WAIT_COPY}`,
          })
        }
        onReconciled?.({
          outcome: fullyDone ? 'recovered' : 'partial_reconciled',
          purchaseOrderNumber: poLabel,
          data: result?.data || result,
        })
      } else if (result?.noSliceMatched) {
        RejectedNotification({
          message: 'Receipts do not match the split',
          description: openPos.length
            ? `No purchase order receipt matched its share of the invoice. Still open on ${openPos.join(
                ', ',
              )}.`
            : 'No purchase order receipt matched its share of the invoice.',
        })
        return
      } else {
        if (!(showedSentSuccess && fullyDone)) {
          SuccessNotification({
            message: fullyDone ? 'Matched & reconciled' : 'PO slice reconciled',
            description: fullyDone
              ? `Attached to ${closedLabel} and Full accepted on Goods Receipting. Invoices Status is Reconciled; GR should show Invoice attached and Full accept.`
              : `Closed ${closedLabel} on Goods Receipting. Invoice still open on ${openPos.join(
                  ', ',
                )}. ${XERO_AUTO_SEND_PARTIAL_WAIT_COPY}`,
          })
        }
        onReconciled?.({
          outcome: fullyDone ? 'reconciled' : 'partial_reconciled',
          purchaseOrderNumber: poLabel,
          data: result?.data || result,
        })
      }
    } catch (error) {
      if (isAlreadyReconciledError(error)) {
        SuccessNotification({
          message: 'Already reconciled',
          description:
            'This invoice was already reconciled. Refreshing your list — Status should show Reconciled.',
        })
        onReconciled?.({ outcome: 'already_reconciled' })
        return
      }

      if (isInvoiceLinkedToMultipleOrdersError(error)) {
        RejectedNotification({
          message: 'Linked to multiple orders',
          description: getInvoiceLinkedToMultipleOrdersMessage(error),
        })
        if (linkedOk) {
          onLinked?.({
            outcome: 'linked_pending_reconcile',
            purchaseOrderNumber: poLabel,
          })
        }
        return
      }

      if (isForceReconcileRequiredError(error)) {
        RejectedNotification({
          message: 'Totals are outside tolerance',
          description: linkedOk
            ? `Attached to ${poLabel}, but totals are outside tolerance so it was not auto-reconciled. Status is Invoice attached; finish on Goods Receipting.`
            : 'This is no longer a full match within tolerance. Attach the invoice instead, then finish on Goods Receipting.',
        })
        // Refresh so the row shows Invoice attached if the link step succeeded.
        if (linkedOk) {
          onLinked?.({
            outcome: 'linked_pending_reconcile',
            purchaseOrderNumber: poLabel,
          })
        }
        return
      }

      const processingError =
        error?.response?.data?.data?.processingError === true ||
        error?.data?.processingError === true
      const linkedButFailed =
        linkedOk ||
        error?.response?.data?.data?.linkedButReconcileFailed === true ||
        error?.data?.linkedButReconcileFailed === true

      RejectedNotification({
        message: getMatchAndReconcileFailureTitle(error),
        description: processingError
          ? linkedButFailed
            ? `Attached to ${poLabel}, but closing the invoice did not finish. If Goods Receipting already shows Receipted, retry Match & reconcile (safe). Otherwise finish receipting on Goods Receipting first.${
                error?.response?.data?.data?.underlyingMessage
                  ? ` (${error.response.data.data.underlyingMessage})`
                  : ''
              }`
            : getReconcileFailureDescription(error)
          : getReconcileFailureDescription(error, {
              fallback:
                'Please review the match and try again. If this invoice is linked to more than one order, remove it from the extras first (Remove from this PO).',
            }),
      })
      if (linkedOk) {
        onLinked?.({
          outcome: 'linked_pending_reconcile',
          purchaseOrderNumber: poLabel,
        })
      }
    } finally {
      setSaving(false)
    }
  }

  const handleNotThisOrder = async () => {
    if (!invoice?.id || !venueId) return

    setSaving(true)
    try {
      const result = await removeInvoiceFromPurchaseOrder(invoice.id, {
        venueId,
        username,
      })
      SuccessNotification({
        message: result?.message || 'Invoice removed from the purchase order',
        description:
          'Matching will refresh. Search for the correct PO here, or close and match again from Invoices.',
      })
      onRemoved?.({
        outcome: 'removed_from_po',
        previousPurchaseOrderNumber:
          result?.previousPurchaseOrderNumber ||
          removeFromPo.purchaseOrderNumber ||
          null,
        data: result,
      })
    } catch (error) {
      RejectedNotification({
        message: 'Could not remove invoice from PO',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Please try again.',
      })
    } finally {
      setSaving(false)
    }
  }

  const allocationsAlreadyBalance = savedAllocationsMatchInvoiceTotal(invoice)
  const primaryLabel = allocationsAlreadyBalance
    ? 'Match & reconcile'
    : presentation.primaryLabel
  const primaryHandler = allocationsAlreadyBalance
    ? handleMatchAndReconcile
    : presentation.primaryIsMatchAndReconcile
    ? handleMatchAndReconcile
    : handleLinkOnly
  const showAddAnotherOrder = shouldShowAddAnotherOrderInMatchModal({
    isCreditNote,
  })
  const isMultiOrderDraft =
    isMatchMultiOrderDraft(extraOrders) && !allocationsAlreadyBalance
  const poSuggestions = useMemo(
    () =>
      getMatchPoSuggestionsPresentation(invoice || {}, {
        purchaseOrderNumber,
        extraOrders,
        isCreditNote,
      }),
    [invoice, purchaseOrderNumber, extraOrders, isCreditNote],
  )
  const selectedMatchPurchaseOrderNumbers = useMemo(
    () =>
      collectMatchSelectedOrders({
        purchaseOrderNumber,
        linkedOrderId,
        extraRows: extraOrders,
      }).map(row => row.purchaseOrderNumber),
    [purchaseOrderNumber, linkedOrderId, extraOrders],
  )
  const invoiceTotalPenniesForSearch =
    invoice?.invoiceTotal == null || invoice?.invoiceTotal === ''
      ? null
      : Number(invoice.invoiceTotal)
  const poSearchSupplierId = getInvoicePoSearchSupplierId(invoice)
  const savedMatchedOrders = useMemo(
    () => getInvoiceMatchedOrdersTableDisplay(invoice || {}),
    [invoice],
  )
  const primaryReconciliationConflict = useMemo(
    () =>
      findPoReconciliationConflictInLibrary({
        purchaseOrderNumber,
        currentInvoiceId: invoice?.id,
        supplierId: poSearchSupplierId,
        invoiceRows: uploadedInvoices,
      }),
    [purchaseOrderNumber, invoice?.id, poSearchSupplierId, uploadedInvoices],
  )
  const hasReconciliationConflict = Boolean(
    primaryReconciliationConflict ||
      extraOrders.some(row =>
        findPoReconciliationConflictInLibrary({
          purchaseOrderNumber: row.purchaseOrderNumber,
          currentInvoiceId: invoice?.id,
          supplierId: poSearchSupplierId,
          invoiceRows: uploadedInvoices,
        }),
      ),
  )
  const canSubmitWithIdentifiedOrder = hasIdentifiedOrderForInvoiceLink({
    linkedOrderId,
    selectedIsRecommended,
    recommendedOrderId,
  })
  const linkActionsDisabled =
    saving || hasReconciliationConflict || !canSubmitWithIdentifiedOrder

  const getReconciliationConflictForPo = po =>
    findPoReconciliationConflictInLibrary({
      purchaseOrderNumber: po,
      currentInvoiceId: invoice?.id,
      supplierId: poSearchSupplierId,
      invoiceRows: uploadedInvoices,
    })

  const handleAddAnotherOrder = () => {
    setExtraOrders(prev => [...prev, createMatchExtraOrderRow()])
  }

  const handleRemoveExtraOrder = key => {
    setExtraOrders(prev => prev.filter(row => row.key !== key))
  }

  const handleExtraOrderChange = (key, update) => {
    const nextPo = update.purchaseOrderNumber || ''
    const extrasExceptThis = extraOrders
      .filter(row => row.key !== key)
      .map(row => row.purchaseOrderNumber)
    const duplicate = getDuplicateMatchPoMessage(nextPo, [
      purchaseOrderNumber,
      ...extrasExceptThis,
    ])
    if (duplicate) {
      RejectedNotification({
        message: 'Purchase order already added',
        description: duplicate,
      })
      return
    }
    setExtraOrders(prev =>
      prev.map(row =>
        row.key === key
          ? {
              ...row,
              purchaseOrderNumber: nextPo,
              linkedOrderId: update.linkedOrderId || null,
              orderedDate: update.orderedDate || null,
              deliveryDate: update.deliveryDate || null,
              sentToXero: Boolean(update.sentToXero),
              status: update.status || '',
            }
          : row,
      ),
    )
  }

  const handleAllocateAcrossOrders = () => {
    const selected = collectMatchSelectedOrders({
      purchaseOrderNumber,
      linkedOrderId,
      extraRows: extraOrders,
    })
    if (selected.length < 2) {
      RejectedNotification({
        message: 'Choose at least two purchase orders',
        description:
          'Add another order, then allocate this invoice across those POs.',
      })
      return
    }
    if (hasReconciliationConflict) {
      RejectedNotification({
        message: 'Order already reconciled',
        description:
          primaryReconciliationConflict?.message ||
          'One or more selected purchase orders are already reconciled against another invoice.',
      })
      return
    }
    setAllocateSeedSelections(selected)
    setShowAllocateStep(true)
  }

  const handleAllocateCancelled = () => {
    setShowAllocateStep(false)
  }

  const handleAllocatedFromMatch = result => {
    setShowAllocateStep(false)
    onLinked?.({
      outcome: 'allocated_multi_po',
      purchaseOrderNumber: collectMatchSelectedOrders({
        purchaseOrderNumber,
        linkedOrderId,
        extraRows: extraOrders,
      })
        .map(row => row.purchaseOrderNumber)
        .join(', '),
      data: result,
    })
  }

  return (
    <>
      <AllocateInvoiceToPosModal
        visible={Boolean(visible && showAllocateStep && invoice)}
        venueId={venueId}
        invoice={invoice}
        seedSelections={allocateSeedSelections}
        onCancel={saving ? undefined : handleAllocateCancelled}
        onAllocated={handleAllocatedFromMatch}
      />
      <Modal
        title={isCreditNote ? 'Apply credit note' : 'Match invoice'}
        visible={Boolean(visible && !showAllocateStep)}
        onCancel={saving ? undefined : onCancel}
        footer={null}
        destroyOnClose
        width={560}
      >
        {!invoice ? null : (
          <Body>
            <InvoiceDiscrepancyAction
              invoice={invoice}
              venueId={venueId}
              disabled={saving}
              onChanged={() => onLinked?.({ outcome: 'discrepancy_reviewed' })}
            />
            <ToneBanner $toneStyles={matchAction.styles}>
              <ToneTag $toneStyles={matchAction.styles}>
                {matchAction.label}
              </ToneTag>
              <ToneCopy>{presentation.bannerGuidance}</ToneCopy>
            </ToneBanner>

            {fixDetails.offer ? (
              <FixDetailsCallout>
                <FixDetailsCopy>
                  {fixDetails.guidance}
                  {fixDetails.elsewhereHint ? (
                    <> {fixDetails.elsewhereHint}</>
                  ) : null}
                </FixDetailsCopy>
                {fixDetails.showButton ? (
                  <Button
                    type='default'
                    disabled={saving}
                    onClick={() => onFixDetails?.(invoice)}
                  >
                    {fixDetails.buttonLabel}
                  </Button>
                ) : fixDetailsInvoicesPath ? (
                  <Link to={fixDetailsInvoicesPath}>
                    <Button type='default' disabled={saving}>
                      Open on Invoices
                    </Button>
                  </Link>
                ) : null}
              </FixDetailsCallout>
            ) : null}

            {presentation.showHappyPathNote ? (
              <HappyPathNote>{presentation.happyPathNote}</HappyPathNote>
            ) : null}

            {presentation.showGoodsReceiptNote ? (
              <GoodsReceiptNote>
                {presentation.goodsReceiptNote}
              </GoodsReceiptNote>
            ) : null}

            {cnCoach.show ? (
              <CnCoachCallout>
                <CnCoachTitle>Missing a credit note?</CnCoachTitle>
                <CnCoachCopy>{cnCoach.hint}</CnCoachCopy>
                {cnCoach.followUp ? (
                  <CnCoachCopy>{cnCoach.followUp}</CnCoachCopy>
                ) : null}
                {cnCoach.doubleCountWarn && cnCoach.doubleCountMessage ? (
                  <CnCoachWarn>{cnCoach.doubleCountMessage}</CnCoachWarn>
                ) : null}
                <CnCoachActions>
                  {uploadCreditNotePath ? (
                    <Link to={uploadCreditNotePath}>
                      <Button type='primary' ghost disabled={saving}>
                        {cnCoach.uploadCtaLabel}
                      </Button>
                    </Link>
                  ) : null}
                  {browseCreditsPath ? (
                    <Link to={browseCreditsPath}>
                      <Button type='default' disabled={saving}>
                        {cnCoach.browseCtaLabel}
                      </Button>
                    </Link>
                  ) : null}
                </CnCoachActions>
              </CnCoachCallout>
            ) : null}

            {showNoOrderGuidance && noOrderGuidance ? (
              <NoOrderCallout>
                <NoOrderTitle>{noOrderGuidance.title}</NoOrderTitle>
                <NoOrderList>
                  {noOrderGuidance.introBody ? (
                    <NoOrderItem>{noOrderGuidance.introBody}</NoOrderItem>
                  ) : null}
                  {noOrderGuidance.paths.map(path => {
                    const href =
                      path.linkKey === 'receipting'
                        ? noOrderLinks.receipting
                        : path.linkKey === 'ordersManual'
                        ? noOrderLinks.ordersManual
                        : null
                    return (
                      <NoOrderItem key={path.key}>
                        <strong>{path.label}</strong> {path.text}{' '}
                        {href && path.linkLabel ? (
                          <NoOrderLink to={href}>{path.linkLabel}</NoOrderLink>
                        ) : null}
                      </NoOrderItem>
                    )
                  })}
                </NoOrderList>
              </NoOrderCallout>
            ) : null}

            <SummaryCard>
              <SummaryGrid>
                <span>Supplier</span>
                <strong>
                  {formatSupplierTitleDisplay(invoice.supplierTitle)}
                </strong>
                <span>Invoice #</span>
                <strong>{invoice.invoiceNumber || '—'}</strong>
                <span>Invoice total</span>
                <strong>
                  {formatInvoiceTotalDisplay(invoice.invoiceTotal)}
                </strong>
                <DeliveryFeeComparison
                  comparison={invoice.matchSummary?.deliveryComparison}
                />
                {invoice.matchSummary?.orderGrossPennies != null ? (
                  <>
                    {invoice.matchSummary.orderNetPennies != null ? (
                      <>
                        <span>
                          {invoice.matchSummary.orderGrossBasis === 'receipted'
                            ? 'Received net (ex VAT)'
                            : 'Order net (ex VAT)'}
                        </span>
                        <strong>
                          {formatInvoiceTotalDisplay(
                            invoice.matchSummary.orderNetPennies,
                          )}
                        </strong>
                        <span>Order VAT</span>
                        <strong>
                          {formatInvoiceTotalDisplay(
                            invoice.matchSummary.orderVatPennies ?? 0,
                          )}
                        </strong>
                      </>
                    ) : null}
                    <span>
                      {invoice.matchSummary.orderGrossBasis === 'receipted'
                        ? 'Received total (inc VAT)'
                        : invoice.matchSummary.orderGrossBasis === 'ordered'
                        ? 'Ordered total (inc VAT)'
                        : 'Order total (inc VAT)'}
                    </span>
                    <strong>
                      {formatInvoiceTotalDisplay(
                        invoice.matchSummary.orderGrossPennies,
                      )}
                    </strong>
                  </>
                ) : null}
                <span>Current PO</span>
                <strong>{invoice.purchaseOrderNumber || '—'}</strong>
                {savedMatchedOrders.isMultiPo ? (
                  <>
                    <span>{savedMatchedOrders.label}</span>
                    <strong title={savedMatchedOrders.subtitle}>
                      {savedMatchedOrders.purchaseOrderNumbers.join(', ')}
                    </strong>
                  </>
                ) : null}
                {netAmbientLine ? (
                  <>
                    <span>On this PO</span>
                    <strong>{netAmbientLine}</strong>
                  </>
                ) : null}
              </SummaryGrid>
            </SummaryCard>

            {recommendedPo ? (
              <RecommendedBox $tone={matchAction.tone}>
                <div>
                  <RecommendedLabel>
                    Recommended purchase order
                  </RecommendedLabel>
                  <RecommendedPo>{recommendedPo}</RecommendedPo>
                  {invoice.matchingReasonDetail ? (
                    <RecommendedDetail>
                      {invoice.matchingReasonDetail}
                    </RecommendedDetail>
                  ) : null}
                </div>
                <Button
                  type={selectedIsRecommended ? 'default' : 'primary'}
                  ghost={selectedIsRecommended}
                  disabled={selectedIsRecommended || saving}
                  onClick={handleUseRecommended}
                >
                  {selectedIsRecommended ? 'Selected' : 'Use recommended'}
                </Button>
              </RecommendedBox>
            ) : invoiceDocumentPo ? (
              <InvoicePoBox>
                <div>
                  <InvoicePoLabel>
                    {isCreditNote
                      ? 'PO / ref on this credit note'
                      : 'PO on this invoice'}
                  </InvoicePoLabel>
                  <RecommendedPo>{invoiceDocumentPo}</RecommendedPo>
                  <RecommendedDetail>
                    {isCreditNote
                      ? 'This number was read from the credit note. It may be an invoice reference rather than your OMS PO — search below to pick the right purchase order to Apply.'
                      : 'This is the PO printed on the invoice. Matching did not confirm an OMS order for it — search below to pick the right order, or use this PO to link manually.'}
                  </RecommendedDetail>
                </div>
                <Button
                  type={selectedIsInvoiceDocumentPo ? 'default' : 'primary'}
                  ghost={selectedIsInvoiceDocumentPo}
                  disabled={selectedIsInvoiceDocumentPo || saving}
                  onClick={handleUseInvoiceDocumentPo}
                >
                  {selectedIsInvoiceDocumentPo ? 'Selected' : 'Use this PO'}
                </Button>
              </InvoicePoBox>
            ) : (
              <NoRecommendation>
                {isCreditNote
                  ? 'No recommended PO from matching. Search and choose the OMS purchase order to Apply this credit to.'
                  : 'No recommended PO from matching. Search and choose an order below.'}
              </NoRecommendation>
            )}

            {poSuggestions.show ? (
              <SuggestedOrdersBox>
                <div>
                  <SuggestedOrdersLabel>
                    {poSuggestions.sourceLabel}
                  </SuggestedOrdersLabel>
                  <SuggestedOrdersTitle>
                    {poSuggestions.title}
                  </SuggestedOrdersTitle>
                  <SuggestedOrdersList>
                    {poSuggestions.purchaseOrderNumbers.join(', ')}
                  </SuggestedOrdersList>
                  <SuggestedOrdersDetail>
                    {poSuggestions.detail}
                  </SuggestedOrdersDetail>
                </div>
                <Button
                  type='primary'
                  ghost
                  disabled={saving}
                  onClick={handleUseSuggestedOrders}
                >
                  {poSuggestions.buttonLabel}
                </Button>
              </SuggestedOrdersBox>
            ) : null}

            <FieldLabel>Purchase order</FieldLabel>
            <FieldHint>
              {isCreditNote
                ? showNotThisOrder
                  ? 'Wrong recommendation? Use Not this order to clear it, or search for the OMS PO this credit belongs to.'
                  : recommendedPo
                  ? 'Search for another PO if the recommendation is wrong, then Apply.'
                  : invoiceDocumentPo
                  ? 'Search for the OMS purchase order this credit note belongs to.'
                  : 'Search and choose a purchase order, then Apply.'
                : showNotThisOrder
                ? 'Wrong recommendation? Use Not this order to clear it, or search for another PO.'
                : recommendedPo
                ? 'Search for another PO if the recommendation is wrong.'
                : invoiceDocumentPo
                ? 'Search for the OMS order that matches this invoice.'
                : 'Search and choose a purchase order.'}
            </FieldHint>
            <PurchaseOrderInput
              venueId={venueId}
              value={purchaseOrderNumber}
              supplierId={poSearchSupplierId}
              invoiceDate={invoice.invoiceDate}
              invoiceTotalPennies={invoiceTotalPenniesForSearch}
              excludePurchaseOrderNumbers={selectedMatchPurchaseOrderNumbers}
              showOrderHints
              onChange={handlePoChange}
              placeholder='Search PO number'
            />
            {primaryReconciliationConflict ? (
              <ExtraOrderWarning>
                {primaryReconciliationConflict.message}
              </ExtraOrderWarning>
            ) : null}

            {showAddAnotherOrder ? (
              <ExtraOrdersSection>
                {isMultiOrderDraft ? (
                  <ExtraOrdersIntro>
                    This invoice can cover more than one order. Confirm each PO
                    below, then allocate the invoice total across them. Search
                    lists open orders near the invoice date first.
                  </ExtraOrdersIntro>
                ) : null}
                {extraOrders.map(row => {
                  const sentWarning = getSentToXeroMatchWarning(row)
                  const reconciliationConflict = getReconciliationConflictForPo(
                    row.purchaseOrderNumber,
                  )
                  return (
                    <ExtraOrderRow key={row.key}>
                      <ExtraOrderInput>
                        <PurchaseOrderInput
                          venueId={venueId}
                          value={row.purchaseOrderNumber}
                          supplierId={poSearchSupplierId}
                          invoiceDate={invoice.invoiceDate}
                          invoiceTotalPennies={invoiceTotalPenniesForSearch}
                          excludePurchaseOrderNumbers={
                            selectedMatchPurchaseOrderNumbers
                          }
                          showOrderHints
                          onChange={update =>
                            handleExtraOrderChange(row.key, update)
                          }
                          placeholder='Search another PO number'
                        />
                        {reconciliationConflict ? (
                          <ExtraOrderWarning>
                            {reconciliationConflict.message}
                          </ExtraOrderWarning>
                        ) : null}
                        {sentWarning ? (
                          <ExtraOrderWarning>{sentWarning}</ExtraOrderWarning>
                        ) : null}
                      </ExtraOrderInput>
                      <Button
                        type='text'
                        icon={<DeleteOutlined />}
                        aria-label='Remove order'
                        disabled={saving}
                        onClick={() => handleRemoveExtraOrder(row.key)}
                      />
                    </ExtraOrderRow>
                  )
                })}
                <AddAnotherOrderButton
                  type='dashed'
                  icon={<PlusOutlined />}
                  disabled={saving}
                  onClick={handleAddAnotherOrder}
                >
                  Add another order
                </AddAnotherOrderButton>
              </ExtraOrdersSection>
            ) : null}

            <FooterRow>
              <Button onClick={onCancel} disabled={saving}>
                Cancel
              </Button>
              {showNotThisOrder && !isMultiOrderDraft ? (
                <Popconfirm
                  title={removeFromPo.confirmCopy}
                  placement='topRight'
                  overlayStyle={{ maxWidth: 360 }}
                  onConfirm={handleNotThisOrder}
                  okText='Remove'
                  cancelText='Cancel'
                  disabled={saving}
                >
                  <Button disabled={saving}>
                    {removeFromPo.matchModalLabel}
                  </Button>
                </Popconfirm>
              ) : null}
              {isMultiOrderDraft ? (
                <Button
                  type='primary'
                  disabled={saving || hasReconciliationConflict}
                  onClick={handleAllocateAcrossOrders}
                >
                  Allocate across these orders
                </Button>
              ) : (
                <>
                  {presentation.showLinkOnlySecondary && (
                    <Button
                      disabled={linkActionsDisabled}
                      onClick={handleLinkOnly}
                    >
                      {presentation.linkOnlyLabel}
                    </Button>
                  )}
                  <Button
                    type='primary'
                    loading={saving}
                    disabled={
                      presentation.primaryIsMatchAndReconcile
                        ? saving ||
                          hasReconciliationConflict ||
                          !canSubmitWithIdentifiedOrder
                        : linkActionsDisabled
                    }
                    onClick={primaryHandler}
                  >
                    {primaryLabel}
                  </Button>
                </>
              )}
            </FooterRow>
          </Body>
        )}
      </Modal>
    </>
  )
}

export default MatchInvoiceModal

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const ToneBanner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.$toneStyles?.border || '#d9d9d9'};
  background: ${props => props.$toneStyles?.background || '#fafafa'};
`

const ToneTag = styled(stripTransientProps(Tag))`
  && {
    align-self: flex-start;
    margin: 0;
    color: ${props => props.$toneStyles?.color || 'inherit'};
    background: #fff;
    border-color: ${props => props.$toneStyles?.border || '#d9d9d9'};
  }
`

const ToneCopy = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.65);
`

const FixDetailsCallout = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #91d5ff;
  background: #e6f7ff;
`

const FixDetailsCopy = styled.p`
  margin: 0;
  flex: 1;
  font-size: 13px;
  line-height: 1.45;
  color: #0050b3;
`

const GoodsReceiptNote = styled.p`
  margin: 0;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.45;
  color: #874d00;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 8px;
`

const HappyPathNote = styled.p`
  margin: 0;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.45;
  color: #135200;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
`

const NoOrderCallout = styled.div`
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #ffccc7;
  background: #fff2f0;
`

const CnCoachCallout = styled.div`
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #ffe58f;
  background: #fffbe6;
`

const CnCoachTitle = styled.div`
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #874d00;
`

const CnCoachCopy = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.75);

  &:last-of-type {
    margin-bottom: 0;
  }
`

const CnCoachWarn = styled.p`
  margin: 8px 0 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: #fff7e6;
  border: 1px solid #ffd591;
  font-size: 12px;
  line-height: 1.45;
  color: #ad6800;
`

const CnCoachActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`

const NoOrderTitle = styled.div`
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #a8071a;
`

const NoOrderList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const NoOrderItem = styled.li`
  font-size: 13px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.75);
`

const NoOrderLink = styled(Link)`
  font-weight: 600;
  color: #cf1322;
  text-decoration: underline;

  &:hover {
    color: #a8071a;
  }
`

const SummaryCard = styled.div`
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fafafa;
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 6px 12px;
  font-size: 13px;

  span {
    color: rgba(0, 0, 0, 0.45);
  }
`

const RecommendedBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid
    ${props =>
      props.$tone === MATCH_ACTION_TONE.GREEN
        ? '#b7eb8f'
        : props.$tone === MATCH_ACTION_TONE.AMBER
        ? '#ffe58f'
        : '#ffccc7'};
  background: ${props =>
    props.$tone === MATCH_ACTION_TONE.GREEN
      ? '#f6ffed'
      : props.$tone === MATCH_ACTION_TONE.AMBER
      ? '#fffbe6'
      : '#fff2f0'};
`

const InvoicePoBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #d9d9d9;
  background: #fafafa;
`

const InvoicePoLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.45);
`

const RecommendedLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.45);
`

const RecommendedPo = styled.div`
  margin-top: 2px;
  font-size: 16px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`

const RecommendedDetail = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
`

const NoRecommendation = styled.p`
  margin: 0;
  padding: 10px 12px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  background: #fafafa;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
`

const SuggestedOrdersBox = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #91d5ff;
  background: #e6f7ff;
`

const SuggestedOrdersLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #096dd9;
`

const SuggestedOrdersTitle = styled.div`
  margin-top: 4px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`

const SuggestedOrdersList = styled.div`
  margin-top: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #0050b3;
  word-break: break-word;
`

const SuggestedOrdersDetail = styled.div`
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.55);
`

const FieldLabel = styled.div`
  font-weight: 600;
  margin-bottom: -6px;
`

const FieldHint = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: -4px;
`

const ExtraOrdersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;
`

const ExtraOrdersIntro = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.65);
`

const ExtraOrderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
`

const ExtraOrderInput = styled.div`
  flex: 1;
  min-width: 0;
`

const ExtraOrderWarning = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: #ad4e00;
`

const AddAnotherOrderButton = styled(Button)`
  align-self: flex-start;
`

const FooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`
